import {
  InfoSmIcon,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import CrmIcon from 'components/CrmIcon';
import { useAuth } from 'context/auth';
import IconCrono from 'crono-fe-common/icons/Icon-Crono';
import { getBrandClassNameForIntegrationType } from 'crono-fe-common/utils/crm-utils';
import { cn } from 'utils/style-utils';

export type SyncType = 'crm' | 'crono';

export interface SyncTypeOption {
  value: SyncType;
  label: string;
  /** Shown from an info icon next to the label. */
  tooltip?: string;
}

interface SyncTypeRadioGroupProps {
  /** Rendered in this order, one card each. */
  options: SyncTypeOption[];
  value: SyncType;
  onChange: (value: SyncType) => void;
  'aria-label': string;
}

/** CRM / Crono choice as a row of radio cards; the CRM card takes the workspace CRM's brand colour. */
const SyncTypeRadioGroup = ({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
}: SyncTypeRadioGroupProps) => (
  <RadioGroup
    aria-label={ariaLabel}
    value={value}
    onValueChange={(next) => onChange(next as SyncType)}
    className="flex flex-row gap-2"
  >
    {options.map((option) => (
      <SyncTypeCard
        key={option.value}
        option={option}
        selected={option.value === value}
      />
    ))}
  </RadioGroup>
);

interface SyncTypeCardProps {
  option: SyncTypeOption;
  selected: boolean;
}

const SyncTypeCard = ({ option, selected }: SyncTypeCardProps) => (
  <label
    className={cn(
      'flex-1 flex items-center gap-3 py-1 pl-1 pr-3.5 rounded-lg border border-neutral-300 cursor-pointer',
      selected ? 'bg-gray-100' : 'bg-white',
    )}
  >
    {option.value === 'crm' ? (
      <CrmTile selected={selected} />
    ) : (
      <CronoTile selected={selected} />
    )}
    <span className="flex-1 flex items-center gap-1.5">
      <span className="text-sm font-semibold whitespace-nowrap text-gray-900">
        {option.label}
      </span>
      {option.tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center" data-testid="sync-type-info">
              <InfoSmIcon className="size-4 text-gray-700" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="z-9999">{option.tooltip}</TooltipContent>
        </Tooltip>
      )}
    </span>
    <RadioGroupItem value={option.value} size="sm" />
  </label>
);

interface SyncTypeTileProps {
  selected: boolean;
}

const TILE_CLASS_NAME =
  'shrink-0 size-8 flex items-center justify-center rounded-lg';
const UNSELECTED_TILE_CLASS_NAME = 'bg-neutral-100 text-neutral-500';

const CrmTile = ({ selected }: SyncTypeTileProps) => {
  const { user } = useAuth();
  const crmBrandClassName =
    getBrandClassNameForIntegrationType(user?.company?.integrationType) ||
    'bg-neutral-100';

  return (
    <span
      className={cn(
        TILE_CLASS_NAME,
        selected
          ? [crmBrandClassName, 'text-white']
          : UNSELECTED_TILE_CLASS_NAME,
      )}
    >
      <CrmIcon className="size-6" color="currentColor" />
    </span>
  );
};

const CronoTile = ({ selected }: SyncTypeTileProps) => (
  <span
    className={cn(
      TILE_CLASS_NAME,
      selected ? 'bg-primary-700 text-white' : UNSELECTED_TILE_CLASS_NAME,
    )}
  >
    <IconCrono className="size-6" color="currentColor" />
  </span>
);

export default SyncTypeRadioGroup;
